const request = require('../../../../../shared/Request');
const activity = require('activity');

export interface ActiveTimer {
  expires_at: string;
  server_time: string;
}

export interface StartTimerActivityResponse {
  success: boolean;
  started_at?: number;
  active_timer?: ActiveTimer | null;
}

export const startTimerActivity = async (
  activityInstanceId: number
): Promise<StartTimerActivityResponse> => {
  const t1 = Date.now();

  return new Promise((resolve, reject) => {
    request.post({
      url: `${window.APP_CONFIG.STUDENT_SERVICE}/api/activities/${activityInstanceId}/start`,
      data: {},
      success: (
        response: StartTimerActivityResponse & {
          data?: StartTimerActivityResponse;
        }
      ) => {
        const t4 = Date.now();
        const networkLatencyMs = t4 - t1;
        // The student service has historically returned either the payload directly
        // or wrapped under `data`, so normalize both shapes here before reading timer fields.
        const data = response?.data ?? response;
        // Some current start responses only indicate success at the top level and return
        // an empty `data` object, so we need a top-level success check instead of relying
        // only on the normalized payload.
        const didStart = Boolean(response?.success ?? data?.success);
        // When the backend acknowledges the start but does not yet return `active_timer`
        // or `started_at`, we seed an approximate client-side start timestamp using the
        // request midpoint. This lets the header countdown begin immediately on the first
        // render instead of waiting for a later activity refresh to populate timer data.
        const approximateStartedAt = t1 + networkLatencyMs / 2;

        if (data?.active_timer) {
          // Preferred path: the backend provides the authoritative timer window. When the
          // student API PR lands, this should be the normal response shape for `/start`.
          activity.set('student_timer_started', true);
          activity.set('student_active_timer', data.active_timer);
          activity.set('student_timer_network_latency', networkLatencyMs);
          activity.set('student_timer_receive_time', Date.now());
        } else if (data?.started_at !== undefined) {
          // Backward-compatible path: some responses expose only the canonical start time.
          // QuizTimer can derive the remaining duration from this value until an
          // authoritative `active_timer` becomes available.
          activity.set('student_timer_started', true);
          activity.set('student_timer_started_at', data.started_at);
          activity.set('student_timer_network_latency', networkLatencyMs);
        } else if (didStart) {
          // Transitional fallback: the server confirmed the timer started, but returned no
          // timer metadata in the body. We still mark the timer as started locally so the UI
          // updates immediately. Once the student API consistently returns `active_timer`
          // from `/start`, this branch can likely be removed.
          activity.set('student_timer_started', true);
          activity.set('student_timer_started_at', approximateStartedAt);
          activity.set('student_timer_network_latency', networkLatencyMs);
        }

        resolve(data);
      },
      error: (error: unknown) => {
        console.error('Error starting activity:', error);
        reject(error);
      },
    });
  });
};
